import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import {
  getWorkdayBpSourceField,
  IAutomationActionTemplateWorkdayTriggerBusinessProcess,
  WORKDAY_BP_CATALOG,
  WorkdayBpFieldDef,
  WorkdayBpSettingDef,
  WorkdayBusinessProcess
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { useMemo } from 'react';
import {
  AutocompleteSingle,
  AutocompleteSingleSimpleKeyed
} from '../../../../../../../components/Autocomplete';
import { TextFieldLoader } from '../../../../../../../components/TextFieldLoader';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import { FormRow } from '../../../../../../../components/Form/FormRow';
import { FlexContainerVertical } from '../../../../../../../layout/Flex';
import {
  toSearchTermRegexp,
  useUserFieldLableMap,
  useUserFields
} from '../../../../../../../services/user';
import { useWorkdayReferenceOptions } from '../../../../../../Org/pages/Integrations/integrations/workday/service';
import { AutomationBuilderActionValidationStateItem } from '../../validation';

// Processes offered in the picker. Rescind hire is hidden for now (not yet
// proven live / needs the Hire: Rescind grant); existing actions still render.
const SELECTABLE_PROCESSES: WorkdayBusinessProcess[] = [
  WorkdayBusinessProcess.CORRECT_HIRE_EMPLOYEE,
  WorkdayBusinessProcess.CHANGE_BUSINESS_TITLE,
  WorkdayBusinessProcess.CHANGE_JOB,
  WorkdayBusinessProcess.CHANGE_ORGANIZATION_ASSIGNMENTS,
  WorkdayBusinessProcess.REQUEST_COMPENSATION_CHANGE
  // WorkdayBusinessProcess.RESCIND_HIRE
];

const REFERENCE_HINT =
  'The Dado field must hold the Workday ID or the exact Workday name.';

type Action = IAutomationActionTemplateWorkdayTriggerBusinessProcess;

/** One setting picked from the tenant's Workday references (free-text ID if the list can't load). */
const WorkdayReferenceSetting = ({
  orgId,
  setting,
  value,
  onChange
}: {
  orgId: string;
  setting: WorkdayBpSettingDef;
  value: string | null;
  onChange: (next: string | null) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const [resp, loading, error] = useWorkdayReferenceOptions(
    orgId,
    setting.referenceType,
    setting.descriptorPrefix
  );
  const options = useMemo(() => resp?.options ?? [], [resp]);
  const label = t(setting.label);

  if (loading && !resp) {
    return (
      <TextFieldLoader
        label={label}
        placeholder={t('Loading from Workday...')}
        fullWidth
      />
    );
  }
  if (error || !options.length) {
    return (
      <TextField
        fullWidth
        label={label}
        value={value ?? ''}
        helperText={t(
          'Could not load the list from Workday — enter the Workday ID.'
        )}
        onChange={(e) => onChange(e.target.value.trim() || null)}
      />
    );
  }
  const selected = options.find((o) => o.id === value) ?? null;
  return (
    <AutocompleteSingle
      label={label}
      value={selected}
      options={options}
      onChange={(o) => onChange(o?.id ?? null)}
      getOptionLabel={(o) => o.descriptor}
      renderOption={(o) => o.descriptor}
      getOptionSelected={(a, b) => a.id === b.id}
      filterOptions={(os, s) => {
        const re = toSearchTermRegexp(s.inputValue);
        return os.filter((o) => o.descriptor.match(re) || o.id.match(re));
      }}
      fullWidth
    />
  );
};

export const ActionSettingsWorkday = ({
  value,
  onChange,
  customFields,
  orgId,
  validation
}: {
  value: Action;
  onChange: (next: Action) => void;
  customFields: CustomUserField[];
  orgId: string;
  validation?: AutomationBuilderActionValidationStateItem | null;
}) => {
  const { t } = useUnsafeTranslation();
  const def = WORKDAY_BP_CATALOG[value.process];
  // Keys are the participant field paths the backend reads with lodash `get`.
  const userFields = useUserFields(customFields);
  const optionsFor = (field: WorkdayBpFieldDef) =>
    userFields.filter((f) =>
      field.valueKind === 'date' ? f.type === 'date' : f.type !== 'date'
    );

  const commit = (patch: Partial<Action>) => onChange({ ...value, ...patch });

  const setMapping = (target: string, sourceField: string | null) =>
    commit({
      fieldMappings: [
        ...(value.fieldMappings ?? []).filter((m) => m.target !== target),
        ...(sourceField ? [{ target, sourceField }] : [])
      ],
      // the legacy scalar is superseded once the start date is mapped here
      ...(target === 'startDate' ? { startDateSourceField: null } : {})
    });

  const setSetting = (key: string, next: string | null) =>
    commit({ settings: { ...(value.settings ?? {}), [key]: next } });

  const errors = [
    validation?.fields?.fieldMappings,
    validation?.fields?.settings
  ].filter(Boolean);

  return (
    <FlexContainerVertical spacing={2} fullWidth alignItems="stretch">
      <FormRow>
        <FormControl fullWidth>
          <InputLabel>{t('Business process')}</InputLabel>
          <Select
            value={value.process}
            onChange={(e) =>
              commit({
                process: e.target.value as WorkdayBusinessProcess,
                // reset process-specific configuration on switch
                fieldMappings: [],
                settings: {},
                startDateSourceField: null
              })
            }
          >
            {SELECTABLE_PROCESSES.map((p) => (
              <MenuItem key={p} value={p}>
                {t(WORKDAY_BP_CATALOG[p].label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </FormRow>

      {def.fields.map((field) => (
        <FormRow key={field.target}>
          <FlexContainerVertical spacing={0.5} fullWidth alignItems="stretch">
            <AutocompleteSingleSimpleKeyed
              label={
                field.required
                  ? t(field.label)
                  : `${t(field.label)} (${t('optional')})`
              }
              fullWidth
              value={getWorkdayBpSourceField(value, field.target) ?? ''}
              options={optionsFor(field)}
              toSelectKey={(f) => f.key}
              toLabel={(f) => f.label}
              onChange={(key) => setMapping(field.target, key || null)}
            />
            {field.valueKind === 'reference' && (
              <Typography variant="caption" color="textSecondary">
                {t(REFERENCE_HINT)}
              </Typography>
            )}
          </FlexContainerVertical>
        </FormRow>
      ))}

      {def.settings.map((setting) => (
        <FormRow key={setting.key}>
          <WorkdayReferenceSetting
            orgId={orgId}
            setting={setting}
            value={value.settings?.[setting.key] ?? null}
            onChange={(next) => setSetting(setting.key, next)}
          />
        </FormRow>
      ))}

      <FormRow>
        <TextField
          fullWidth
          label={t('Comment (optional)')}
          value={value.comment ?? ''}
          onChange={(e) => commit({ comment: e.target.value || null })}
        />
      </FormRow>

      {errors.map((e) => (
        <Typography key={e} variant="caption" color="error">
          {e}
        </Typography>
      ))}
    </FlexContainerVertical>
  );
};

export const ActionSettingsWorkdayReadOnly = ({ value }: { value: Action }) => {
  const { t } = useUnsafeTranslation();
  const labelByKey = useUserFieldLableMap();
  const def = WORKDAY_BP_CATALOG[value.process];
  const mapped = (def?.fields ?? [])
    .map((f) => {
      const source = getWorkdayBpSourceField(value, f.target);
      return source
        ? `${t(f.label)} ${t('from')} ${labelByKey[source] || source}`
        : null;
    })
    .filter(Boolean);
  return (
    <Typography variant="body2" color="textSecondary">
      {t(def?.label ?? value.process)}
      {mapped.length ? ` — ${mapped.join(', ')}` : ''}
    </Typography>
  );
};
